import React, { useRef, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User, UserPermission } from '../../types';
import apiService from '../../services/api';
import styles from '../../styles/Sidebar.module.css';

interface SidebarProps {
  collapsed: boolean;
  isMobile: boolean;
  onToggle: () => void;
  user: User | null;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, isMobile, onToggle, user }) => {
  const location = useLocation();
  const sidebarRef = useRef<HTMLElement>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
  const [isMainAdmin, setIsMainAdmin] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState<number>(0);
  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const handleScroll = () => {
      setIsScrolling(true);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 1000);
    };

    sidebar.addEventListener('scroll', handleScroll);

    return () => {
      sidebar.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Load user permissions
  useEffect(() => {
    const loadUserPermissions = async () => {
      if (!user) {
        setPermissionsLoading(false);
        return;
      }

      try {
        setPermissionsLoading(true);
        const response = await apiService.getUserPermissions() as any;
        console.log('User permissions response:', response);
        if (response && response.status === 'success') {
          // Handle both response structures:
          // 1. Direct: { status: 'success', permissions: [...], is_main_admin: true }
          // 2. Nested: { status: 'success', data: { permissions: [...], is_main_admin: true } }
          const responseData = response.data || response;
          const permissions = (responseData.permissions || []) as UserPermission[];
          const isMainAdmin = Boolean(responseData.is_main_admin);

          setUserPermissions(permissions);
          setIsMainAdmin(isMainAdmin);
          console.log('Set permissions:', permissions.length, 'isMainAdmin:', isMainAdmin);
        } else {
          console.warn('Unexpected response format:', response);
          setUserPermissions([]);
          setIsMainAdmin(false);
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
        // On error, show no permissions (empty array)
        setUserPermissions([]);
        setIsMainAdmin(false);
      } finally {
        setPermissionsLoading(false);
      }
    };

    loadUserPermissions();
  }, [user]);

  // Load unread notification count
  useEffect(() => {
    const loadNotificationCount = async () => {
      if (!user) {
        setUnreadNotificationCount(0);
        return;
      }

      try {
        const response = await apiService.getNotificationStats() as any;
        if (response && response.status === 'success' && response.data) {
          const unreadCount = response.data.unread_count || 0;
          setUnreadNotificationCount(unreadCount);
        }
      } catch (error) {
        console.error('Error loading notification count:', error);
        setUnreadNotificationCount(0);
      }
    };

    loadNotificationCount();

    // Refresh notification count every 30 seconds
    const interval = setInterval(loadNotificationCount, 30000);

    return () => clearInterval(interval);
  }, [user]);

  const getUserFullName = () => {
    if (!user) return 'User';
    return `${user.first_name} ${user.last_name}`.trim() || user.email;
  };

  const isActive = (path: string) => {
    if (path === '/admin/group-management') {
      return location.pathname === '/admin/group-management' ||
        location.pathname.startsWith('/admin/groups') ||
        location.pathname.startsWith('/admin/group-management');
    }
    if (path === '/my-groups') {
      return location.pathname === '/my-groups' ||
        location.pathname.startsWith('/my-groups/') ||
        location.pathname.startsWith('/group/');
    }
    return location.pathname === path || location.pathname.startsWith(path);
  };

  const hasModulePermission = (module: string): boolean => {
    if (permissionsLoading) {
      return false;
    }

    const validActions = ['view', 'add', 'edit', 'delete', 'create', 'update'];
    return userPermissions.some(
      perm => perm.module === module && validActions.includes(perm.action)
    );
  };

  const shouldShowItem = (item: any): boolean => {
    if (item.path === '/permission-management') {
      return isMainAdmin;
    }

    if (item.path === '/platform-reports') {
      return user?.role === 'admin';
    }

    if (!item.module) {
      return true;
    }

    return hasModulePermission(item.module);
  };

  const getAllNavItems = () => [
    {
      section: 'Wallet Workspace',
      items: [
        { path: '/dashboard', label: 'Dashboard', icon: 'home', module: null }, // Always visible
        { path: '/user-management', label: 'User Management', icon: 'users', module: 'user_management' },
        { path: '/campus-administration', label: 'Campus Hierarchy', icon: 'campus', module: 'campus_hierarchy' },
        { path: '/group-management/groups', label: 'Group Management', icon: 'groups', module: 'group_management' },
        { path: '/point-scheme', label: 'Point Scheme', icon: 'points', module: 'point_scheme' },
        { path: '/task-management', label: 'Task Management', icon: 'tasks', module: 'task_management' },
        { path: '/counselling', label: 'Counselling', icon: 'counselling', module: null },
        { path: '/leader-board', label: 'Leader Board', icon: 'chart', module: 'leader_board' },
        { path: '/badges', label: 'Badges & Achievements', icon: 'badge', module: 'badge_achievements' },
        { path: '/platform-reports', label: 'Platform Reports', icon: 'report', module: null }, // Always visible for now
        { path: '/notifications', label: 'Notifications', icon: 'notifications', badge: unreadNotificationCount, module: 'notifications' },
        { path: '/my-profile', label: 'Profile', icon: 'profile', module: null }, // Always visible
        { path: '/permission-management', label: 'Accessibility', icon: 'accessibility', module: null }, // Always visible for now
        { path: '/system-settings', label: 'System Settings', icon: 'settings', module: null }, // Always visible for now

      ]
    },
  ];

  const getNavItems = () => {
    if (permissionsLoading) {
      console.log('getNavItems: Loading permissions, showing basic items');
      return [
        { path: '/dashboard', label: 'Dashboard', icon: 'home' },
        { path: '/my-profile', label: 'Profile', icon: 'profile' },
      ];
    }

    console.log('getNavItems: Filtering items', { isMainAdmin, userPermissionsCount: userPermissions.length });

    const allNavItems = getAllNavItems();
    const filteredItems = allNavItems.map(section => ({
      ...section,
      items: section.items.filter(item => {
        const shouldShow = shouldShowItem(item);
        console.log(`Item ${item.label} (${item.module || 'no module'}): ${shouldShow ? 'showing' : 'hiding'}`, {
          isMainAdmin,
          path: item.path
        });
        return shouldShow;
      })
    })).filter(section => section.items.length > 0);

    console.log('getNavItems: Filtered items', filteredItems);

    if (filteredItems.length > 0 && filteredItems[0].items.length > 0) {
      return filteredItems;
    }

    console.log('getNavItems: Using fallback items');
    return [
      { path: '/dashboard', label: 'Dashboard', icon: 'home' },
      { path: '/my-profile', label: 'Profile', icon: 'profile' },
    ];
  };

  const getIcon = (iconName: string) => {
    const icons: { [key: string]: React.ReactElement } = {
      home: (
        <>
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <path d="M9 22V12h6v10" />
          <path d="M12 12h3" />
        </>
      ),
      users: (
        <>
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </>
      ),
      groups: (
        <>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </>
      ),
      points: (
        <>
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </>
      ),
      campus: (
        <>
          <circle cx="12" cy="5" r="2" />
          <circle cx="7" cy="12" r="2" />
          <circle cx="17" cy="12" r="2" />
          <circle cx="5" cy="19" r="2" />
          <circle cx="12" cy="19" r="2" />
          <circle cx="19" cy="19" r="2" />
          <line x1="12" y1="7" x2="7" y2="10" />
          <line x1="12" y1="7" x2="17" y2="10" />
          <line x1="7" y1="14" x2="5" y2="17" />
          <line x1="7" y1="14" x2="12" y2="17" />
          <line x1="17" y1="14" x2="12" y2="17" />
          <line x1="17" y1="14" x2="19" y2="17" />
        </>
      ),
      chart: (
        <>
          <path d="M3 3v18h18" />
          <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" />
        </>
      ),
      badge: (
        <>
          <circle cx="12" cy="8" r="7"></circle>
          <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
        </>
      ),
      report: (
        <>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <line x1="10" y1="9" x2="8" y2="9" />
        </>
      ),
      settings: (
        <>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </>
      ),
      wallet: (
        <>
          <path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2zm-9-2h10V8H12zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5"></path>
        </>
      ),
      tasks: (
        <>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </>
      ),
      activity: (
        <>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </>
      ),
      notifications: (
        <>
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </>
      ),
      profile: (
        <>
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </>
      ),
      accessibility: (
        <>
          <path d="M9.5 1.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0M6 5.5l-4.535-.442A.531.531 0 0 1 1.531 4H14.47a.531.531 0 0 1 .066 1.058L10 5.5V9l.452 6.42a.535.535 0 0 1-1.053.174L8.243 9.97c-.064-.252-.422-.252-.486 0l-1.156 5.624a.535.535 0 0 1-1.053-.174L6 9z" />
        </>
      ),
      counselling: (
        <>
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </>
      ),
    };
    return icons[iconName] || icons.home;
  };

  const renderNavItems = (items: any[]) => {
    return items.map((item, index) => (
      <li key={index}>
        <Link
          to={item.path}
          className={`${styles.navLink} ${isActive(item.path) ? styles.active : ''}`}
        >
          {item.icon === 'accessibility' ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-universal-access " viewBox="0 0 16 16">
              {getIcon(item.icon)}
            </svg>
          ) : (
            <svg className={styles.navIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {getIcon(item.icon)}
            </svg>
          )}
          <span className={styles.navLabel}>{item.label}</span>
          {item.badge !== undefined && item.badge > 0 && (
            <span className={styles.navBadge}>{item.badge}</span>
          )}
        </Link>
      </li>
    ));
  };

  const renderNavSections = (sections: any[]) => {
    return sections.map((section, sectionIndex) => (
      <React.Fragment key={sectionIndex}>
        <li className={styles.navSectionHeader}>
          <span className={styles.sectionTitle}>{section.section}</span>
        </li>
        {renderNavItems(section.items)}
      </React.Fragment>
    ));
  };

  return (
    <nav
      ref={sidebarRef}
      className={`${styles.sideNav} ${collapsed ? styles.collapsed : ''} ${isMobile ? styles.mobileView : ''} ${isScrolling ? styles.scrolling : ''}`}
    >
      <div className={styles.sideNavHeader}>
        <h2
          className={styles.logoTitle}
          onClick={() => window.location.href = '/dashboard'}
        >
          <img
            src="/images/wallet-transparent.png"
            alt="WorkWallet Logo"
            width="30"
            height="30"
          />
          <span className={styles.navLabel}>Lendi Effort Recording</span>
        </h2>
      </div>

      <ul className={styles.navLinks}>
        {(() => {
          const navItems = getNavItems();
          if (Array.isArray(navItems) && navItems.length > 0 && 'section' in navItems[0]) {
            return renderNavSections(navItems as any[]);
          }
          return renderNavItems(navItems as any[]);
        })()}
      </ul>

      <div className={`${styles.navToggle} ${collapsed ? styles.rotated : ''}`} onClick={onToggle}>
        <svg viewBox="0 0 24 24">
          <path d="M15 19l-7-7 7-7" />
        </svg>
      </div>
    </nav>
  );
};

export default Sidebar;